import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Apple, LayoutGrid, List, Plus, Smartphone, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/shared/lib/auth';
import { decimal } from '@/shared/lib/format';
import { PageHeader } from '@/shared/components/page-header';
import { SelectableListLayout } from '@/shared/components/selectable-list-layout';
import { KpiCard } from '@/shared/components/kpi-card';
import { EmptyState } from '@/shared/components/empty-state';
import { useConfirm } from '@/shared/components/confirm';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Pagination } from '@/shared/ui/pagination';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import type { AdminClient, ClientKind, ClientLifecycleStatus } from '@/shared/types/client';
import {
  buildClientGridCards,
  computeClientStats,
  deleteClient,
  fetchClients,
  toggleClientInactive,
} from '../api/use-clients';
import { ClientCard } from './client-card';
import { ClientCloneDialog } from './client-clone-dialog';
import { ClientGridCard } from './client-grid-card';

const PAGE_SIZE = 25;
const VIEW_MODE_KEY = 'clients-view-mode';

type ViewMode = 'list' | 'grid';
type ActiveFilter = 'all' | 'active' | 'inactive';

function readViewMode(searchParams: URLSearchParams): ViewMode {
  const fromUrl = searchParams.get('view');
  if (fromUrl === 'grid' || fromUrl === 'list') return fromUrl;
  const stored = localStorage.getItem(VIEW_MODE_KEY);
  return stored === 'grid' ? 'grid' : 'list';
}

export function ClientsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>(() => readViewMode(searchParams));
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('active');
  const [lifecycleFilter, setLifecycleFilter] = useState<'all' | ClientLifecycleStatus>('all');
  const [kindFilter, setKindFilter] = useState<'all' | ClientKind>('all');
  const [registrationFilter, setRegistrationFilter] = useState<'all' | 'draft'>('all');
  const [cloneTarget, setCloneTarget] = useState<AdminClient | null>(null);

  const { data = [], isLoading, refetch } = useQuery({ queryKey: ['clients'], queryFn: fetchClients });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const fromUrl = searchParams.get('view');
    if (fromUrl === 'grid' || fromUrl === 'list') {
      setViewMode(fromUrl);
      localStorage.setItem(VIEW_MODE_KEY, fromUrl);
    }
  }, [searchParams]);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return data
      .filter((c) => {
        if (q) {
          const matchesSearch =
            (c.name ?? '').toLowerCase().includes(q) ||
            (c.cnpj ?? '').toLowerCase().includes(q) ||
            (c.email ?? '').toLowerCase().includes(q);
          if (!matchesSearch) return false;
        }
        if (activeFilter === 'active' && c.inactive) return false;
        if (activeFilter === 'inactive' && !c.inactive) return false;
        if (lifecycleFilter !== 'all' && (c.lifecycleStatus ?? 'prospect') !== lifecycleFilter) return false;
        if (kindFilter !== 'all' && (c.clientKind ?? 'fixe') !== kindFilter) return false;
        if (registrationFilter === 'draft' && c.registrationStatus !== 'draft') return false;
        return true;
      })
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'pt-BR'));
  }, [data, debouncedSearch, activeFilter, lifecycleFilter, kindFilter, registrationFilter]);

  const stats = useMemo(() => computeClientStats(filtered), [filtered]);
  const gridCards = useMemo(() => buildClientGridCards(filtered), [filtered]);

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearch, activeFilter, lifecycleFilter, kindFilter, registrationFilter, viewMode]);

  const pageRows = useMemo(() => {
    const start = pageIndex * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, pageIndex]);



  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_KEY, mode);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (mode === 'list') next.delete('view');
      else next.set('view', mode);
      return next;
    });
  };

  const toggleActive = useCallback(
    async (client: AdminClient) => {
      try {
        await toggleClientInactive(client._id);
        toast.success(client.inactive ? 'Cliente reativado.' : 'Cliente inativado.');
        refetch();
      } catch {
        toast.error('Falha ao alterar status.');
      }
    },
    [refetch],
  );

  const removeOne = useCallback(
    async (client: AdminClient) => {
      const userId = client.users?.[0];
      if (!userId) {
        toast.error('Cliente sem usuário vinculado.');
        return;
      }
      const ok = await confirm({
        title: 'Remover cliente',
        description: `Remover “${client.name}”? Esta ação não pode ser desfeita.`,
        confirmLabel: 'Remover',
        variant: 'destructive',
      });
      if (!ok) return;
      try {
        await deleteClient(client._id, userId);
        toast.success('Cliente removido.');
        refetch();
      } catch {
        toast.error('Falha ao remover cliente.');
      }
    },
    [confirm, refetch],
  );



  if (!user || user.role !== 'Admin') {
    return <Navigate to="/login" replace />;
  }

  const filterBar = (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <Select value={activeFilter} onValueChange={(v) => setActiveFilter(v as ActiveFilter)}>
        <SelectTrigger className="w-[140px]">
          <SelectValue placeholder="Situação" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos</SelectItem>
          <SelectItem value="active">Ativo</SelectItem>
          <SelectItem value="inactive">Inativo</SelectItem>
        </SelectContent>
      </Select>
      <Select value={lifecycleFilter} onValueChange={(v) => setLifecycleFilter(v as typeof lifecycleFilter)}>
        <SelectTrigger className="w-[140px]">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos status</SelectItem>
          <SelectItem value="cliente">Cliente</SelectItem>
          <SelectItem value="prospect">Prospect</SelectItem>
        </SelectContent>
      </Select>
      <Select value={kindFilter} onValueChange={(v) => setKindFilter(v as typeof kindFilter)}>
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Tipo" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos tipos</SelectItem>
          <SelectItem value="fixe">Fixe</SelectItem>
          <SelectItem value="parceiro">Parceiro</SelectItem>
          <SelectItem value="whitelabel">Whitelabel</SelectItem>
        </SelectContent>
      </Select>
      <Select value={registrationFilter} onValueChange={(v) => setRegistrationFilter(v as typeof registrationFilter)}>
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Cadastro" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos cadastros</SelectItem>
          <SelectItem value="draft">Rascunhos</SelectItem>
        </SelectContent>
      </Select>
      <div className="ml-auto inline-flex rounded-xl border border-border/60 p-1">
        <Button
          type="button"
          variant={viewMode === 'list' ? 'secondary' : 'ghost'}
          size="sm"
          className="h-8 px-2.5"
          onClick={() => handleViewModeChange('list')}
          aria-pressed={viewMode === 'list'}
        >
          <List className="size-4" />
        </Button>
        <Button
          type="button"
          variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
          size="sm"
          className="h-8 px-2.5"
          onClick={() => handleViewModeChange('grid')}
          aria-pressed={viewMode === 'grid'}
        >
          <LayoutGrid className="size-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <PageHeader
        title="Clientes"
        actions={
          <Button onClick={() => navigate('/clientes/novo')}>
            <Plus className="size-4" /> Adicionar cliente
          </Button>
        }
      />

      <div className="mb-6 grid gap-4 grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Total clientes" value={decimal(stats.totalClients)} icon={Users} loading={isLoading} index={0} />
        <KpiCard label="Total cadastros" value={decimal(stats.totalCadastros)} icon={Users} loading={isLoading} index={1} />
        <KpiCard label="Total iPhone" value={decimal(stats.totalIos)} icon={Apple} loading={isLoading} index={2} />
        <KpiCard label="Total Android" value={decimal(stats.totalAndroid)} icon={Smartphone} loading={isLoading} index={3} />
      </div>

      {filterBar}

      {viewMode === 'list' ? (
        <SelectableListLayout
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Buscar por nome, CNPJ ou e-mail…"
          selectAllId="select-all-clients"
          itemCount={pageRows.length}
          allOnPageSelected={false}
          someOnPageSelected={false}
          onToggleAllOnPage={() => undefined}
          hideSelection
          withFloatingBar={false}
          isLoading={isLoading}
          isEmpty={filtered.length === 0}
          emptyTitle="Nenhum cliente"
          emptyDescription="Cadastre o primeiro cliente da plataforma."
          footer={
            filtered.length > PAGE_SIZE ? (
              <Pagination page={pageIndex} pageSize={PAGE_SIZE} total={filtered.length} onPageChange={setPageIndex} />
            ) : null
          }
        >
          {pageRows.map((client, index) => {
            const passbookId = client.passbooks?.find((p) => !p.onlyShortUrl)?._id ?? client.passbooks?.[0]?._id;
            return (
              <li key={client._id}>
                <ClientCard
                  client={client}
                  index={index}
                  onEdit={() => navigate(`/clientes/${client._id}`)}
                  onInspect={passbookId ? () => navigate(`/loja/${passbookId}`) : undefined}
                  onToggleActive={() => toggleActive(client)}
                  onRemove={client.users?.[0] ? () => removeOne(client) : undefined}
                  onClone={() => setCloneTarget(client)}
                />
              </li>
            );
          })}
        </SelectableListLayout>
      ) : (
        <div className="space-y-4">
          <div className="relative">
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, CNPJ ou e-mail…"
              className="h-10 rounded-xl"
            />
          </div>
          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-44 rounded-2xl" />
              ))}
            </div>
          ) : gridCards.length === 0 ? (
            <EmptyState
              icon={Users}
              title="Nenhum cliente"
              description="Ajuste os filtros ou cadastre o primeiro cliente da plataforma."
            />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {gridCards.map((card) => (
                <ClientGridCard key={`${card.id}-${card.passbookId}`} card={card} />
              ))}
            </div>
          )}
        </div>
      )}


      <ClientCloneDialog
        client={cloneTarget}
        open={cloneTarget !== null}
        onOpenChange={(open) => {
          if (!open) setCloneTarget(null);
        }}
        onCloned={() => refetch()}
      />
    </>
  );
}
